import { FLAG_DEFAULTS, type FlagKey, type Flags } from './defaults';

/**
 * The GrowthBook client (architecture.md §3: flags and A/B). Loaded only when
 * `VITE_GROWTHBOOK_CLIENT_KEY` is set (a public SDK key), as its own chunk after the first
 * render, so the funnel never waits for it: until it answers (or if it never does) every flag
 * is its local default. No personal data: the only attribute is a random id for bucketing.
 */
export interface FlagSource {
  /** GrowthBook's value for `key`, or undefined (unknown feature, wrong type). Evaluating an
   *  experiment here is what counts as the visitor seeing it (the tracking callback fires). */
  get<K extends FlagKey>(key: K): Flags[K] | undefined;
}

export interface GrowthBookOptions {
  clientKey: string;
  apiHost?: string;
  /** An experiment was shown: `experiment_viewed { experimentId, variationId }`. */
  onExperiment?: (experimentId: string, variationId: string) => void;
  timeoutMs?: number;
}

const ID_KEY = 'db_gb_id';

/** A random id for stable bucketing across visits (not linked to anything else). */
export function anonymousId(): string {
  try {
    const kept = localStorage.getItem(ID_KEY);
    if (kept) return kept;
    const id = crypto.randomUUID();
    localStorage.setItem(ID_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export async function startGrowthBook({
  clientKey,
  apiHost = 'https://cdn.growthbook.io',
  onExperiment,
  timeoutMs = 1500,
}: GrowthBookOptions): Promise<FlagSource> {
  const { GrowthBook } = await import('@growthbook/growthbook');
  const gb = new GrowthBook({
    apiHost,
    clientKey,
    attributes: { id: anonymousId() },
    trackingCallback: (experiment, result) =>
      onExperiment?.(experiment.key, String(result.key ?? result.variationId)),
  });
  try {
    await gb.init({ timeout: timeoutMs });
  } catch {
    /* no features: the defaults stand */
  }
  return {
    get(key) {
      if (!gb.getFeatures()[key]) return undefined;
      const v: unknown = gb.getFeatureValue(key, FLAG_DEFAULTS[key] as never);
      return typeof v === typeof FLAG_DEFAULTS[key] ? (v as Flags[typeof key]) : undefined;
    },
  };
}
